import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

interface PartnerListItem {
  id: string;
  userId: string;
  name: string;
  email: string;
  document?: string;
  phone?: string;
  status: 'active' | 'inactive';
  machineCount: number;
  createdAt: string;
}

interface MachineAssignable {
  id: string;
  code: string;
  location: string;
  city?: string;
  partnerIds?: string[];
}

interface PartnerDetail {
  partner: PartnerListItem;
  email: string;
  machines: MachineAssignable[];
}

interface CreatePartnerResult {
  partner: PartnerListItem;
  email: string;
  tempPassword: string;
}

export const PartnerManagementPage: React.FC = () => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PartnerListItem | null>(null);
  const [credentialsModal, setCredentialsModal] = useState<CreatePartnerResult | null>(null);
  const [assignmentPartner, setAssignmentPartner] = useState<PartnerListItem | null>(null);

  const partnersQuery = useQuery({
    queryKey: ['admin-partners'],
    queryFn: async () => {
      const res = await api.get('/admin/partners');
      return res.data as PartnerListItem[];
    },
  });

  const createMut = useMutation({
    mutationFn: async (data: { name: string; email: string; document?: string; phone?: string }) => {
      const res = await api.post('/admin/partners', data);
      return res.data as CreatePartnerResult;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin-partners'] });
      setShowForm(false);
      setEditing(null);
      setCredentialsModal(data);
    },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PartnerListItem> }) => {
      const res = await api.patch(`/admin/partners/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-partners'] });
      setShowForm(false);
      setEditing(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/admin/partners/${id}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-partners'] }),
  });

  const regenMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/admin/partners/${id}/regenerate-password`);
      return res.data as { email: string; tempPassword: string };
    },
    onSuccess: (data) => {
      // Reuse the credentials modal so the admin sees the new password once.
      setCredentialsModal({
        partner: { ...(editing || {}) } as PartnerListItem,
        email: data.email,
        tempPassword: data.tempPassword,
      });
    },
  });

  const handleSubmit = async (data: { name: string; email: string; document?: string; phone?: string }) => {
    try {
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, data });
      } else {
        await createMut.mutateAsync(data);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao salvar parceiro');
    }
  };

  const handleDelete = async (p: PartnerListItem) => {
    if (!confirm(`Excluir o parceiro ${p.name}? Esta ação não pode ser desfeita. As máquinas atribuídas serão liberadas.`)) return;
    try {
      await deleteMut.mutateAsync(p.id);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const handleRegenerate = async (p: PartnerListItem) => {
    if (!confirm(`Gerar nova senha temporária para ${p.name}? A senha atual deixará de funcionar.`)) return;
    setEditing(p);
    try {
      await regenMut.mutateAsync(p.id);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao gerar nova senha');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Parceiros</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gerencie parceiros locais que recebem repasse pelas máquinas instaladas em seus pontos.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white font-bold rounded-lg hover:from-orange-700 hover:to-orange-600 transition-all shadow-lg"
        >
          + Novo Parceiro
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {partnersQuery.isLoading ? (
          <div className="p-12 text-center text-gray-500">Carregando…</div>
        ) : partnersQuery.error ? (
          <div className="p-12 text-center text-red-600">Erro ao carregar parceiros</div>
        ) : (partnersQuery.data || []).length === 0 ? (
          <div className="p-12 text-center text-gray-500">Nenhum parceiro cadastrado.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Máquinas</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(partnersQuery.data || []).map((p) => (
                <tr key={p.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.phone || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{p.machineCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-2">
                    <button
                      onClick={() => setAssignmentPartner(p)}
                      className="text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Máquinas
                    </button>
                    <button
                      onClick={() => { setEditing(p); setShowForm(true); }}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleRegenerate(p)}
                      className="text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Nova senha
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="text-red-600 hover:text-red-700 font-medium"
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <PartnerFormModal
          partner={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={handleSubmit}
          isSubmitting={createMut.isPending || updateMut.isPending}
        />
      )}

      {credentialsModal && (
        <PartnerCredentialsModal
          email={credentialsModal.email}
          tempPassword={credentialsModal.tempPassword}
          onClose={() => setCredentialsModal(null)}
        />
      )}

      {assignmentPartner && (
        <PartnerMachineAssignmentModal
          partner={assignmentPartner}
          onClose={() => setAssignmentPartner(null)}
        />
      )}
    </div>
  );
};

interface FormState {
  name: string;
  email: string;
  document: string;
  phone: string;
}

const PartnerFormModal: React.FC<{
  partner: PartnerListItem | null;
  onCancel: () => void;
  onSubmit: (data: FormState) => void;
  isSubmitting: boolean;
}> = ({ partner, onCancel, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState<FormState>({
    name: partner?.name || '',
    email: partner?.email || '',
    document: partner?.document || '',
    phone: partner?.phone || '',
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h3 className="text-xl font-bold mb-4">{partner ? 'Editar Parceiro' : 'Novo Parceiro'}</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(form);
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              required
              type="email"
              disabled={!!partner}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
            />
            {partner && (
              <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado após o cadastro.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">CPF/CNPJ</label>
              <input
                value={form.document}
                onChange={(e) => setForm({ ...form, document: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Telefone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando…' : partner ? 'Salvar' : 'Criar parceiro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PartnerCredentialsModal: React.FC<{
  email: string;
  tempPassword: string;
  onClose: () => void;
}> = ({ email, tempPassword, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-xl font-bold mb-2">Senha temporária gerada</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-900">
          <strong>Atenção:</strong> esta senha não será mostrada novamente. Copie agora e
          envie ao parceiro. No primeiro login ele será obrigado a defini-la.
        </div>
        <div className="space-y-2 mb-4">
          <div>
            <span className="text-xs text-gray-500">Email</span>
            <div className="font-mono text-sm bg-gray-50 px-3 py-2 rounded">{email}</div>
          </div>
          <div>
            <span className="text-xs text-gray-500">Senha temporária</span>
            <div className="flex items-center gap-2">
              <div className="flex-1 font-mono text-lg bg-gray-50 px-3 py-2 rounded">{tempPassword}</div>
              <button
                onClick={copy}
                className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
        >
          Fechar
        </button>
      </div>
    </div>
  );
};

const PartnerMachineAssignmentModal: React.FC<{
  partner: PartnerListItem;
  onClose: () => void;
}> = ({ partner, onClose }) => {
  const qc = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['admin-partner-detail', partner.id],
    queryFn: async () => {
      const res = await api.get(`/admin/partners/${partner.id}`);
      return res.data as PartnerDetail;
    },
  });

  const machinesQuery = useQuery({
    queryKey: ['admin-machines-all'],
    queryFn: async () => {
      const res = await api.get('/admin/machines');
      return (res.data as MachineAssignable[]) || [];
    },
  });

  // Already in the cache from the table behind this modal — used only to turn the
  // partner ids on each machine into names.
  const allPartners = qc.getQueryData<PartnerListItem[]>(['admin-partners']) || [];

  const assignMut = useMutation({
    mutationFn: async (machineIds: string[]) => {
      const res = await api.post(`/admin/partners/${partner.id}/machines`, { machineIds });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.id] });
      qc.invalidateQueries({ queryKey: ['admin-partners'] });
      qc.invalidateQueries({ queryKey: ['admin-machines-all'] });
    },
  });

  const unassignMut = useMutation({
    mutationFn: async (machineId: string) => {
      const res = await api.delete(`/admin/partners/${partner.id}/machines/${machineId}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-partner-detail', partner.id] });
      qc.invalidateQueries({ queryKey: ['admin-partners'] });
      qc.invalidateQueries({ queryKey: ['admin-machines-all'] });
    },
  });

  const assigned = detailQuery.data?.machines || [];
  const allMachines = machinesQuery.data || [];
  // A machine can have several partners, so the only ones to hide are the ones this
  // partner already holds — a machine held by someone else is still offerable.
  const assignedIds = new Set(assigned.map((m) => m.id));
  const available = allMachines.filter((m) => !assignedIds.has(m.id));

  // Names for the "já com" hint, so the admin can see an overlap before creating it.
  const otherPartnerNames = (m: MachineAssignable) =>
    (m.partnerIds || [])
      .filter((id) => id !== partner.id)
      .map((id) => allPartners.find((p) => p.id === id)?.name)
      .filter(Boolean) as string[];

  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set());

  const toggleAdd = (id: string) => {
    const next = new Set(selectedToAdd);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedToAdd(next);
  };

  const handleAdd = async () => {
    if (selectedToAdd.size === 0) return;
    await assignMut.mutateAsync(Array.from(selectedToAdd));
    setSelectedToAdd(new Set());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold">Máquinas de {partner.name}</h3>
            <p className="text-sm text-gray-500">{partner.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
        </div>

        <section className="mb-6">
          <h4 className="font-semibold mb-2">Atribuídas ({assigned.length})</h4>
          {assigned.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma máquina atribuída.</p>
          ) : (
            <ul className="divide-y border rounded-lg">
              {assigned.map((m) => {
                // The detail payload has no partner ids, so read them off the machine list.
                const listed = allMachines.find((am) => am.id === m.id);
                const others = listed ? otherPartnerNames(listed) : [];
                return (
                  <li key={m.id} className="px-4 py-3 flex justify-between items-center">
                    <div>
                      <div className="font-medium">{m.code}</div>
                      <div className="text-sm text-gray-500">{m.location}{m.city ? ` — ${m.city}` : ''}</div>
                      {others.length > 0 && (
                        <div className="text-xs text-orange-600 mt-0.5">
                          Compartilhada com: {others.join(', ')}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => unassignMut.mutate(m.id)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Remover
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <h4 className="font-semibold mb-2">Disponíveis para atribuir ({available.length})</h4>
          {available.length === 0 ? (
            <p className="text-sm text-gray-500">Este parceiro já tem todas as máquinas.</p>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-2">
                Uma máquina pode ter mais de um parceiro — por exemplo uma conta geral da
                empresa e a conta do gerente do ponto. Ambos verão o repasse da máquina.
              </p>
              <ul className="divide-y border rounded-lg max-h-64 overflow-y-auto">
                {available.map((m) => {
                  const others = otherPartnerNames(m);
                  return (
                    <li key={m.id} className="px-4 py-3 flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedToAdd.has(m.id)}
                        onChange={() => toggleAdd(m.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{m.code}</div>
                        <div className="text-sm text-gray-500">{m.location}{m.city ? ` — ${m.city}` : ''}</div>
                        {others.length > 0 && (
                          <div className="text-xs text-orange-600 mt-0.5">
                            Já com: {others.join(', ')}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <button
                onClick={handleAdd}
                disabled={selectedToAdd.size === 0 || assignMut.isPending}
                className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                Atribuir selecionadas ({selectedToAdd.size})
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};
